import "../css/MyPhotoPage.css";
import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import GIFCreator from "../components/GIFCreator";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const VEJTHANI_URL = "https://www.vejthani.com";

const BG_STYLE = { backgroundImage: "url(/assets/photopage/bg_texture.png)" };

// While a coupon is scanned but its photos are still uploading from the booth, re-check
// quietly so the page flips to the photos on its own - the guest never refreshes.
// Check often for the first minute (that is when a just-finished upload lands, and the
// wait should feel instant), then ease off for anyone leaving the page open.
const POLL_FAST_MS = 3000;
const POLL_SLOW_MS = 10000;
const FAST_WINDOW_MS = 60000;

const PHOTO_NAME = "vejthani32years-photo.png";
const GIF_NAME = "vejthani32years-gif.gif";

// A web page cannot write to the phone's photo library directly - the OS share sheet
// is the only route in ("Save Image" lands in Photos, a plain download lands in Files).
const CAN_SHARE = typeof navigator !== "undefined" && typeof navigator.share === "function";

// The iOS share glyph, so the button looks like what it opens.
const ShareIcon = () => (
    <svg className="btnIcon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3v13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M8 6.8 12 2.8l4 4" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 12.5V19a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6.5"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);

const Logo = () => (
    <img className="brandLogo" src="/assets/photopage/logo_vejthani32.png"
         alt="Vejthani International Hospital - 32 Years of Care and Excellence" />
);

const VisitButton = () => (
    <a className="btn btnVisit" href={VEJTHANI_URL} target="_blank" rel="noopener noreferrer">
        VISIT OUR WEBSITE
    </a>
);

const Footer = () => (
    <footer className="pageFooter">
        <div>&copy;Vejthani International Hospital</div>
        <div>JCI Accredited International Hospital in Bangkok, Thailand</div>
    </footer>
);

const PhotoPage = () => {
    const { code: pathCode } = useParams();
    const [searchParams] = useSearchParams();

    // New coupons arrive as /<code>; keep ?userid= working for older links.
    const code = (pathCode || searchParams.get("userid") || "").trim();

    // The booth's on-screen QR carries ?fresh=1, so a guest who scans it the moment they
    // finish is told their photos are uploading rather than being invited to the booth.
    const isFresh = searchParams.get("fresh") === "1";

    const [userData, setUserData] = useState(null);
    const [gifUrl, setGifUrl] = useState("");
    const [isLoading, setIsLoading] = useState(!!code);
    // If an upload never arrives, stop promising it forever and show the normal page.
    const [gaveUpWaiting, setGaveUpWaiting] = useState(false);

    // Both images are turned into Files as soon as they exist, BEFORE the guest taps.
    // navigator.share() has to run inside the tap itself - fetching the image first
    // spends the user gesture and iOS rejects the call.
    const [photoFile, setPhotoFile] = useState(null);
    const [gifFile, setGifFile] = useState(null);

    const fetchPhotos = useCallback(async () => {
        if (!code) return null;
        try {
            const snap = await getDoc(doc(db, "users", code));
            if (!snap.exists()) return null;

            const data = snap.data();
            // The sticker is the essential image - without it the session is
            // still uploading, so keep waiting rather than showing a broken page.
            if (!data.sticker) return null;

            // Frames have different photo counts (2 or 3), so the gif is built from
            // whichever gifN images actually exist - see GIFCreator.
            return data;
        } catch (err) {
            console.error("Could not load photos:", err);
            return null;
        }
    }, [code]);

    useEffect(() => {
        if (!code) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        let timer;
        const openedAt = Date.now();

        const check = async () => {
            const data = await fetchPhotos();
            if (cancelled) return;

            setIsLoading(false);
            if (data) {
                setUserData(data);
                return;
            }

            const waited = Date.now() - openedAt;
            timer = setTimeout(check, waited < FAST_WINDOW_MS ? POLL_FAST_MS : POLL_SLOW_MS);
        };

        check();

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [code, fetchPhotos]);

    // Stop showing "on the way" after a couple of minutes - by then something has gone wrong
    // at the booth, so the normal page is more useful than an endless promise.
    useEffect(() => {
        if (!isFresh) return;
        const t = setTimeout(() => setGaveUpWaiting(true), 120000);
        return () => clearTimeout(t);
    }, [isFresh]);

    // Prepare the sticker for sharing the moment it is known.
    useEffect(() => {
        const url = userData?.sticker;
        if (!url || !CAN_SHARE) return;

        let cancelled = false;
        (async () => {
            try {
                const blob = await (await fetch(url)).blob();
                if (!cancelled) {
                    setPhotoFile(new File([blob], PHOTO_NAME, { type: blob.type || "image/png" }));
                }
            } catch (err) {
                // Leave photoFile null - the button quietly falls back to a download.
                console.error("Could not prepare photo for sharing:", err);
            }
        })();

        return () => { cancelled = true; };
    }, [userData]);

    // Same for the gif, which is built in the browser as a data: URL.
    useEffect(() => {
        if (!gifUrl || !CAN_SHARE) return;

        let cancelled = false;
        (async () => {
            try {
                const blob = await (await fetch(gifUrl)).blob();
                if (!cancelled) setGifFile(new File([blob], GIF_NAME, { type: "image/gif" }));
            } catch (err) {
                console.error("Could not prepare GIF for sharing:", err);
            }
        })();

        return () => { cancelled = true; };
    }, [gifUrl]);

    // Runs straight off the tap - no awaits before share(), or the gesture is lost.
    const handleSave = (file, url, fileName) => {
        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            navigator.share({ files: [file] }).catch(err => {
                // AbortError just means the guest dismissed the sheet.
                if (err && err.name !== "AbortError") handleDownload(url, fileName);
            });
            return;
        }

        // Desktop, or a browser without file sharing.
        handleDownload(url, fileName);
    };

    // Firebase Storage links are cross-origin, so the download attribute alone is
    // ignored. Pull the file down first and save that; if the fetch is blocked,
    // fall back to simply opening the image.
    const handleDownload = async (url, fileName) => {
        if (!url) return;

        if (url.startsWith("data:")) {
            triggerDownload(url, fileName);
            return;
        }

        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            triggerDownload(objectUrl, fileName);
            setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
        } catch (err) {
            console.error("Download failed, opening instead:", err);
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const triggerDownload = (href, fileName) => {
        const link = document.createElement("a");
        link.href = href;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (isLoading) {
        return (
            <div className="page">
                <div className="bgTexture" style={BG_STYLE} />
                <Logo />
                <p className="statusText">Loading...</p>
                <Footer />
            </div>
        );
    }

    // Just finished at the booth and the upload is still in flight. The poll above flips
    // this to the photos on its own, so the guest can simply keep the page open.
    if (!userData && isFresh && !gaveUpWaiting) {
        return (
            <div className="page">
                <div className="bgTexture" style={BG_STYLE} />
                <Logo />

                <h1 className="landingHeadline">
                    Your photos are<br />on the way!
                </h1>
                <p className="landingHeadlineTh">กำลังเตรียมรูปของคุณ</p>

                <div className="spinner" aria-hidden="true" />

                <p className="statusText">
                    This page will show your photos automatically.<br />
                    หน้านี้จะแสดงรูปของคุณโดยอัตโนมัติ
                </p>

                <VisitButton />
                <Footer />
            </div>
        );
    }

    // No photos yet - either the site was opened directly, or the coupon has been
    // scanned but the guest has not been to the booth. Both get the same invite.
    if (!userData) {
        return (
            <div className="page">
                <div className="bgTexture" style={BG_STYLE} />
                <Logo />

                <img className="boothImage" src="/assets/photopage/booth.png"
                     alt="Vejthani 32 Years photobooth" />

                <h1 className="landingHeadline">
                    Get your free photo<br />
                    at photobooth on 1<sup>st</sup> floor.
                </h1>
                <p className="landingHeadlineTh">ถ่ายรูปฟรีที่ตู้ถ่ายรูป ชั้น 1</p>

                <VisitButton />
                <Footer />
            </div>
        );
    }

    return (
        <div className="page">
            <div className="bgTexture" style={BG_STYLE} />
            <Logo />

            <h2 className="sectionTitle">GET YOUR <span className="accent">PHOTO</span></h2>

            <img className="stickerImage" src={userData.sticker} alt="Your photobooth strip" />

            <button className="btn btnDownload"
                    onClick={() => handleSave(photoFile, userData.sticker, PHOTO_NAME)}>
                SAVE PHOTO <ShareIcon />
            </button>
            <p className="saveHint">
                {CAN_SHARE ? 'Then choose "Save Image" to keep it in your photos'
                           : "Saves to your downloads folder"}
            </p>

            <h2 className="sectionTitle">GET YOUR <span className="accent">GIF</span></h2>

            <GIFCreator userData={userData} setPageGifUrl={setGifUrl} />

            <button className="btn btnDownload"
                    disabled={!gifUrl}
                    onClick={() => handleSave(gifFile, gifUrl, GIF_NAME)}>
                SAVE GIF <ShareIcon />
            </button>
            <p className="saveHint">
                {CAN_SHARE ? 'Then choose "Save Image" to keep it in your photos'
                           : "Saves to your downloads folder"}
            </p>

            <VisitButton />
            <Footer />
        </div>
    );
}

export default PhotoPage;
